import { clean, prune, stripTags } from 'underscore.string';
import { getSeoDescription } from 'src/utils/parserHTML';
import { getCommunityLogoUrl } from 'src/utils/linkUtils';
import config from 'config/config';

const isServer = typeof window === 'undefined';

export const shouldIndexPost = post => {
  return !post.isPrivate && post.totalResponses > 0 && (post.body || '').length > 280;
};

export const getPostMetaTitle = (post, community) => {

  // our seo friends have advised us that we need titles to be as specific as possible while not exceeding 65 characters.
  // so, we drop the HealthUnlocked from the title (not very specific) and do the following:
  // 1. the meta title ends with the community name, up to 20 characters (after which it is cut off)
  // 2. the meta title starts with the title of the post, as much of it as possible up to a max of 65 characters along with the community name
  // 3. if there are more than 10 characters left available, we include as many characters as we can from the body of the post up to the max of 65.
  const charsForCommunity = 20;
  const communityName = community.name || '';
  const communityNameSnippet = communityName.length > charsForCommunity
    ? communityName.substring(0, charsForCommunity - 3) + '...'
    : communityName;

  const charsForPostTitle = 60 - communityNameSnippet.length;
  const postTitle = post.title || '';
  const postTitleSnippet = postTitle.length > charsForPostTitle
    ? postTitle.substring(0, charsForPostTitle - 3) + '...'
    : postTitle;

  const charsForSnippet = charsForPostTitle - postTitleSnippet.length - 3;
  const bodySnippet = charsForSnippet > 10
    ? ': ' + prune(getSeoDescription(post.enriched.body || []), charsForSnippet)
    : '';

  return `${postTitleSnippet}${bodySnippet} - ${communityNameSnippet}`;
};

export const getPostSeo = (post = {}, community = {}) => {

  return {
    // TODO replace hardcoded url with an environment variable.
    canonical: `https://healthunlocked.com/${community.slug}/posts/${post.id}/${post.urlEncodedTitle}`,
    published_time: post.dateCreated,
    modified_time: post.dateLastActivity,
    title: getPostMetaTitle(post, community),
    description: prune(getSeoDescription(post.enriched.body || []), 200),
    image: community.logoImageHash ? getCommunityLogoUrl(community.logoImageHash) : null,
    robots: shouldIndexPost(post) ? 'index, follow' : 'noindex, follow',
  };
};

export const getPollSeo = (poll, community) => {
  if (!poll || !community) return {};
  const choices = poll.choices && poll.choices.length > 0 && poll.choices.map(c => c.title.trim()).join(', ');
  const description = choices ? `${poll.title} ${choices}` : poll.title;
  return {
    // TODO replace hardcoded url with an environment variable.
    canonical: `https://healthunlocked.com/${community.slug}/polls/${poll.id}/${poll.urlEncodedTitle}/result`,
    published_time: poll.dateCreated,
    modified_time: poll.dateLastActivity,
    title: getPostMetaTitle(poll, community),
    description: prune(description, 200),
    image: community.logoImageHash ? getCommunityLogoUrl(community.logoImageHash) : null,
    robots: shouldIndexPost(poll) ? 'index, follow' : 'noindex, follow',
  };
};

export const getCommunitySeo = (community = {}) => {
  if (!community) return {};
  return {
    canonical: `https://healthunlocked.com/${community.slug}`,
    title: `${community.name} | HealthUnlocked`,
    description: community.description,
    image: getCommunityLogoUrl(community.logoImageHash),
    robots: 'index',
  };
};

export const getCommunityPostsSeo = (
  community = {}, communityTopTags = [], archiveCurrentYear, archiveCurrentMonth, currentPage, isPollsList,
) => {
  if (!community) return {};
  let description = 'Read community conversations. Get support and information from people sharing their experiences';
  if (communityTopTags.length) {
    description += ' with' + communityTopTags.map(tag => tag.name.toLowerCase()).map(tagName => ` ${tagName}`) + ' and more.';
  }

  if (archiveCurrentYear && archiveCurrentMonth) {
    return {
      canonical: `https://healthunlocked.com/${community.slug}/posts/archive/${archiveCurrentYear}/${archiveCurrentMonth}?page=${currentPage}`,
      title: `Posts - ${community.name} | HealthUnlocked`,
      description: description,
      image: getCommunityLogoUrl(community.logoImageHash),
      robots: 'index',
    };
  } else if (isPollsList) {
    return {
      canonical: `https://healthunlocked.com/${community.slug}/polls`,
      title: `Polls - ${community.name} | HealthUnlocked`,
      description: description,
      image: getCommunityLogoUrl(community.logoImageHash),
      robots: 'index',
    };
  } else {
    return {
      canonical: `https://healthunlocked.com/${community.slug}/posts`,
      title: `Posts - ${community.name} | HealthUnlocked`,
      description: description,
      image: getCommunityLogoUrl(community.logoImageHash),
      robots: 'index',
    };
  }
};

export const getCommunitySearchSeo = (community = {}) => {
  if (!community) return {};
  return {
    canonical: `https://healthunlocked.com/${community.slug}/search/posts`,
    title: `Search - ${community.name} | HealthUnlocked`,
    description: community.description,
    image: getCommunityLogoUrl(community.logoImageHash),
    robots: 'noindex',
  };
};

export const getCommunityMembersSeo = (community = {}) => {
  if (!community) return {};
  return {
    canonical: `https://healthunlocked.com/${community.slug}/members`,
    title: `Members - ${community.name} | HealthUnlocked`,
    description: '',
    image: getCommunityLogoUrl(community.logoImageHash),
    robots: 'noindex',
  };
};

export const getCommunityAboutSeo = (community = {}) => {
  if (!community) return {};
  return {
    canonical: `https://healthunlocked.com/${community.slug}/about`,
    title: `About - ${community.name} | HealthUnlocked`,
    description: community.description,
    image: getCommunityLogoUrl(community.logoImageHash),
    robots: community.isPrivate ? 'noindex' : 'index',
  };
};

export const getArticleSeo = (title, snippet, imageUrl, articleUrl) => {
  if (!title) return {};
  return {
    canonical: `https://healthunlocked.com/${articleUrl}`,
    title: `${title} | HealthUnlocked`,
    description: snippet,
    image: imageUrl,
    robots: 'index',
  };
};

export const getProgramsAboutSeo = (programSlug, programName = '', description = '') => {
  if (!programSlug) return {};
  return {
    canonical: `https://healthunlocked.com/programs/${programSlug}`,
    title: `${programName} | HealthUnlocked`,
    description: description,
    image: `${config.cdn.programs}${programSlug}/landing-page-banner.jpg`,
    robots: 'index',
  };
};

export const getProgramsModuleSeo = (programSlug, programName = '', moduleCode, currentModule) => {
  if (!programSlug) return {};

  const moduleName = currentModule ? currentModule.name : '';
  const moduleSections = currentModule ? currentModule.sections : undefined;

  // For the description meta tag, we take the first 200 characters of the first section of the module
  // As there can be images, links or html tags, we remove those to only have text
  let description = '';
  if (moduleSections) {
    // trim: trim whitespace characters from begining and ending of the string
    // stripTags: removing any HTML tags (for iframes for example)
    // replace: replace carriage return or newline character with a whitespace character
    description = stripTags(moduleSections[0].content.trim()).replace(/[\n\r]/g, ' ');

    // Replace links [title](some_url) and images ![alt](some_url) found in the markdown with a whitespace character
    description = description.replace(/\!?\[.*?\]\(.*?\)/gi, ' ');

    // prune: truncate string to 200 characters
    // clean: compress some whitespaces to one (the ones we've added with replace above)
    description = prune(clean(description), 200);
  }

  return {
    canonical: `https://healthunlocked.com/programs/${programSlug}/${moduleCode}`,
    title: `${moduleName} | ${programName} | HealthUnlocked`,
    description,
    robots: 'index',
  };
};

export const getResourceSeo = (resourceId, name, image, description) => {
  return {
    canonical: `https://healthunlocked.com/resources/${resourceId}`,
    title: `${name} | HealthUnlocked`,
    image,
    meta: { robots: 'index' },
    description,
  };
};

export const getTagsListSeo = (letter) => {
  return {
    canonical: `https://healthunlocked.com/tags/${letter.toLowerCase()}`,
    title: 'Health terms A to Z | HealthUnlocked',
    description: 'Find all health terms (conditions, treatments, wellbeing interests etc) discussed on HealthUnlocked',
    robots: 'index',
  };
};

export const getUnsubscribeSeo = () => {
  return {
    description: '',
    title: 'Unsubscribe | HealthUnlocked',
    robots: 'noindex',
  };
};

export const isUserBot = !isServer && /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent);
